import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
const ToastContext = createContext(null);
/** Ohangga qarab chap qirradagi belgi va matn rangi. */
const toneClass = {
    // Chap chiziq YASHIL: "saqlandi" xabari urg'u rangida bo'lsa, u ma'lumot
    // xabaridan (ko'k) farq qilmasdi.
    success: 'shadow-[var(--shadow-2),var(--shadow-rail-success)]',
    // QISMAN natija — na muvaffaqiyat, na xato. Yashil chiziq bo'lsa odam
    // "hammasi o'tdi" deb o'qirdi va o'tmaganini qidirmasdi.
    warn: 'shadow-[var(--shadow-2),var(--shadow-rail-warn)]',
    danger: 'shadow-[var(--shadow-2),var(--shadow-rail-danger)]',
    info: 'shadow-[var(--shadow-2),var(--shadow-rail-info)]',
};
/** Xabar ekranda turadigan vaqt — o'qishga yetadi, ishga xalaqit bermaydi. */
const DISMISS_AFTER_MS = 5000;
/**
 * Bir vaqtda ko'rinadigan xabarlar soni.
 *
 * Uchtadan ko'pi o'qilmaydi: ular ekranning pastini to'ldirib, ish
 * yuzasini yopib qo'yadi. Chegaradan oshganda ENG ESKISI ketadi —
 * odam oxirgi nima bo'lganini bilishi muhimroq.
 */
const MAX_VISIBLE = 3;
/**
 * Qisqa muddatli bildirishnomalar. Konteyner `aria-live="polite"` —
 * skrinrider foydalanuvchi ishini bo'lmasdan xabarni o'qiydi.
 */
export const ToastProvider = ({ children, closeLabel }) => {
    const [items, setItems] = useState([]);
    const timers = useRef(new Map());
    const counter = useRef(0);
    /*
     * Ro'yxatning JORIY nusxasi.
     *
     * `setItems` ning yangilovchi funksiyasi darhol ishlamaydi — React uni
     * qayta chizishda chaqiradi. Takrorlangan xabarni topish esa AYNAN
     * `show` chaqirilgan paytda kerak: aks holda taymerni qaysi xabarga
     * qayta qo'yishni bilib bo'lmaydi va takror xabar eskisining
     * taymerida yonib turaverardi.
     */
    const itemsRef = useRef([]);
    const commit = useCallback((next) => {
        itemsRef.current = next;
        setItems(next);
    }, []);
    const clearTimer = useCallback((id) => {
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);
    const dismiss = useCallback((id) => {
        clearTimer(id);
        commit(itemsRef.current.filter((item) => item.id !== id));
    }, [clearTimer, commit]);
    /** Yopilish taymerini BOSHIDAN qo'yadi. */
    const arm = useCallback((id) => {
        clearTimer(id);
        timers.current.set(id, setTimeout(() => dismiss(id), DISMISS_AFTER_MS));
    }, [clearTimer, dismiss]);
    const show = useCallback((message, tone = 'info') => {
        /*
         * AYNI xabar ikkinchi marta QO'SHILMAYDI.
         *
         * Xato ko'pincha takrorlanadi — odam tugmani yana bosadi va server
         * yana o'sha javobni qaytaradi. Har safar yangi xabar qo'shilsa,
         * bir xil matn ekranda ustma-ust to'planardi va ularning hech biri
         * yangi ma'lumot bermasdi.
         *
         * Mavjudi yangilanadi: taymeri qaytadan qo'yiladi, ya'ni xabar
         * OXIRGI urinishdan boshlab sanaydi.
         */
        const same = itemsRef.current.find((item) => item.message === message && item.tone === tone);
        if (same) {
            arm(same.id);
            return;
        }
        counter.current += 1;
        const id = `toast-${counter.current}`;
        const next = [...itemsRef.current, { id, message, tone }];
        // Chegaradan oshgani — eng eskisi — ketadi, taymeri bilan birga.
        while (next.length > MAX_VISIBLE) {
            const oldest = next.shift();
            if (oldest === undefined)
                break;
            clearTimer(oldest.id);
        }
        commit(next);
        arm(id);
    }, [arm, clearTimer, commit]);
    // Provider yo'q qilinganda osilib qolgan taymerlarni tozalaymiz.
    const timerMap = timers.current;
    useEffect(() => () => {
        for (const timer of timerMap.values())
            clearTimeout(timer);
        timerMap.clear();
    }, [timerMap]);
    const api = useMemo(() => ({ show }), [show]);
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, _jsx("div", { "aria-live": "polite", "aria-atomic": "false", className: cn('pointer-events-none fixed inset-x-0 bottom-0 z-[var(--z-toast)]', 'flex flex-col items-center gap-2 p-4', 'sm:inset-x-auto sm:right-0 sm:items-end'), children: items.map((item) => (_jsxs("div", { className: cn('motion-slide-in pointer-events-auto flex w-full max-w-sm items-start gap-3', 'rounded-[var(--radius-md)] border border-border bg-surface px-4 py-3', toneClass[item.tone]), children: [_jsx("p", { className: "min-w-0 flex-1 text-sm text-text", children: item.message }), _jsx("button", { type: "button", onClick: () => dismiss(item.id), "aria-label": closeLabel?.() ?? 'Yopish', className: cn('-mr-1 -mt-0.5 shrink-0 rounded-[var(--radius-sm)] px-1.5 py-0.5', 'text-sm text-text-faint', 'transition-[background-color,color] duration-[var(--motion-fast)]', 'hover:bg-surface-2 hover:text-text'), children: "\u00D7" })] }, item.id))) })] }));
};
/** `ToastProvider` ichida bo'lishi shart — aks holda xato tashlaydi. */
export const useToast = () => {
    const api = useContext(ToastContext);
    if (!api)
        throw new Error('useToast faqat <ToastProvider> ichida ishlaydi');
    return api;
};
//# sourceMappingURL=Toast.js.map